/**
 * BYO v2 — source hydration for the edit modal (split out of
 * {@link useByoEditSource}).
 *
 * Loads an existing BYO (`flow_type: custom`) source by id and maps it into
 * the InputPanel value shape, following the legacy DirectFlow pattern
 * (`useStartSource`):
 *
 *   - load the source by id        → useSourcesAdapter().getSourceById(id)
 *   - extract credential/container → data_credentials / code_container_id
 *   - load its code container      → dispatch(getCodeContainer(code_container_id))
 *   - verify the credential         → verifyCredentialFromForm(FORM, credId)
 *
 * Decoding the container itself is {@link useByoCodeContainerHydration}'s job;
 * this hook only puts it in the store and exposes its id.
 */

import { useEffect, useState } from 'react';
import { getCodeContainer, verifyCredentialFromForm } from 'js/actions';
import { FORM_TYPE_CREATE_DIRECT_FLOW } from 'js/constants/formTypes';
import useAppDispatch from 'js/hooks/useAppDispatch';
import useSourcesAdapter from 'new/state/sources/adapter/useSourcesAdapter';
import type { ByoInputValue } from '../types';

const FORM = FORM_TYPE_CREATE_DIRECT_FLOW;

export const EMPTY_BYO_INPUT: ByoInputValue = {
  connectionType: null,
  credentialId: null,
  folderPath: null,
  selectedFile: null,
  fileType: null,
};

export interface UseByoSourceHydration {
  /** True until the source (and its code container) have loaded. */
  loading: boolean;
  /** Hydration error message, if any. */
  error: string | null;
  /** Source or code-container load failed — Save must stay disabled. */
  hydrationFailed: boolean;
  /** Credential verification failed; non-blocking since the code still loaded. */
  credentialError: string | null;
  /** The fetched source entity (null until loaded). */
  source: any;
  name: string;
  setName: (name: string) => void;
  input: ByoInputValue;
  setInput: (next: ByoInputValue) => void;
  /** The code container attached to the source (re-armed by save()). */
  codeContainerId: number | string | null;
  setCodeContainerId: (id: number | string | null) => void;
}

/**
 * @param id BYO source id to hydrate from. When absent nothing is loaded and
 *           the hook returns an empty (create-like) draft.
 */
export function useByoSourceHydration(
  id?: number,
  { viewOnly = false }: { viewOnly?: boolean } = {},
): UseByoSourceHydration {
  const dispatch = useAppDispatch();
  const { getSourceById } = useSourcesAdapter();

  const [loading, setLoading] = useState<boolean>(Boolean(id));
  const [error, setError] = useState<string | null>(null);
  // Source or code-container GET failed: the draft still holds DEFAULT_CODE /
  // empty packages while pointing at the real container, so saving would
  // overwrite the user's code. Blocks save (PR #3212 follow-up, fix 2).
  const [hydrationFailed, setHydrationFailed] = useState(false);
  const [credentialError, setCredentialError] = useState<string | null>(null);

  const [source, setSource] = useState<any>(null);
  const [name, setName] = useState('');
  const [input, setInput] = useState<ByoInputValue>(EMPTY_BYO_INPUT);
  // The code container is normalized into Redux after getCodeContainer; we keep
  // the id we loaded so save can recreate/replace it.
  const [codeContainerId, setCodeContainerId] = useState<number | string | null>(null);

  useEffect(() => {
    if (!id) {
      setLoading(false);
      return;
    }
    let cancelled = false;
    setLoading(true);
    setError(null);
    setHydrationFailed(false);
    setCredentialError(null);
    (async () => {
      try {
        const fetched = await getSourceById(id);
        if (!fetched) throw new Error(`Source ${id} not found.`);
        if (cancelled) return;

        const credId = fetched.data_credentials ?? fetched.data_credentials_id ?? null;
        const containerId = fetched.code_container_id ?? null;

        setSource(fetched);
        setName(fetched.name ?? '');
        setInput({
          connectionType: fetched.source_type ?? null,
          credentialId: credId,
          folderPath:
            (typeof fetched.source_config?.path === 'string'
              ? fetched.source_config.path
              : (fetched.source_config?.path as any)?.path) ?? null,
          selectedFile: null,
          fileType: fetched.source_config?.['file.type'] ?? null,
        });
        setCodeContainerId(containerId);
        // Tab (Use Existing / Provide Your Own) is decided once the attached
        // container itself arrives — see useByoCodeContainerHydration, which
        // reads the container's own `reusable` field. Confirmed bug, fixed
        // 2026-08-20: this used to trust a `ui.byo_code_predefined_id`
        // source_config marker instead (reasoning: "the container is always a
        // fresh non-reusable copy, so its own `reusable` flag is useless") —
        // live data showed that reasoning was wrong: a source's attached
        // container's `reusable` flag is real and authoritative, and the
        // marker went stale (pointed at code no longer relevant) the moment a
        // container was replaced by a save. `container.reusable` can't go
        // stale the same way — it's read fresh off the exact container this
        // source has right now.

        if (containerId != null) {
          await dispatch(getCodeContainer(containerId));
        }
        // View code only shows the code — nothing there uses the credential.
        if (credId != null && !viewOnly) {
          try {
            await dispatch(verifyCredentialFromForm(FORM, credId));
          } catch (e: any) {
            if (!cancelled) setCredentialError(e?.message ?? 'Unable to validate credentials.');
          }
        }
      } catch (e: any) {
        if (cancelled) return;
        setError(e?.message ?? 'Failed to load the BYO source.');
        setHydrationFailed(true);
      } finally {
        if (!cancelled) setLoading(false);
      }
    })();
    return () => {
      cancelled = true;
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [id]);

  return {
    loading,
    error,
    hydrationFailed,
    credentialError,
    source,
    name,
    setName,
    input,
    setInput,
    codeContainerId,
    setCodeContainerId,
  };
}

export default useByoSourceHydration;
